import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  DateTimeProvider,
  Id,
  PaginatedResult,
  createObj,
  enumGetValues,
  throwInvalidOperation,
  throwInvalidParameter,
  validateRequiredNumber,
  validateRequiredString,
} from '../../../common'
import { ActualIssueProductsRepository } from '../../actualIssueProducts'
import { CreateEventMode, ModelEvent, ModelEventsProcessor } from '../../events'
import {
  AddProductSupplyOpts,
  PlanReportFindOptions,
  ProductPlansService,
  ProductReport,
  ProductSuppliesRepository,
  ProductSupply,
} from './productPlans'
import { ProductCategory } from '../../productCategories'
import { Permissions } from '../../authorization'
import { forIn, round } from 'lodash'

enum EventType {
  PRODUCT_SUPPLY_ADD = 'PRODUCT_SUPPLY_ADD',
}

type AddProductSupplyPayload = {
  dataEntryDate: string
  category: ProductCategory
  amount: number
}

interface DBProductPlansServiceOpts extends BaseDBServiceOpts<CoreDict> {
  productSuppliesRepository: ProductSuppliesRepository
  actualIssueProductsRepository: ActualIssueProductsRepository
  dateTimeProvider: DateTimeProvider
}

export class DBProductPlansService
  extends BaseDBService<DBProductPlansServiceOpts, CoreDict>
  implements ProductPlansService
{
  constructor(opts: DBProductPlansServiceOpts) {
    super('DBProductPlansService', opts)
    this.logger.debug({}, '<655feabd> Init')
  }

  private validateAmount = (amount?: number) =>
    validateRequiredNumber('<c6451eb1> Amount', amount, { minValue: 0.000000001 })
  private validateCategory = (ctx: Context, category?: ProductCategory) => {
    category = validateRequiredString('<27b036fb> Category', category) as ProductCategory
    if (enumGetValues(ProductCategory).includes(category)) return category
    else
      throwInvalidParameter(
        this.translate(ctx, d => d.productCategories.categoryDoesNotExist),
        { uid: '<3c7d2ca2>' },
      )
  }
  private validateDataEntryDate = (dataEntryDate?: string) =>
    validateRequiredString('<dc09d97a> Data entry date', dataEntryDate)

  async add(ctx: Context, opts: AddProductSupplyOpts): Promise<Id> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.PRODUCT_SUPPLIES_MANAGE)

    const entityId = this.opts.idGenerator.generate()

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.PRODUCT_SUPPLY_ADD,
        payload: createObj<AddProductSupplyPayload>({
          dataEntryDate: this.validateDataEntryDate(opts.dataEntryDate.toISOString()),
          category: this.validateCategory(ctx, opts.category),
          amount: this.validateAmount(opts.amount),
        }),
      }),
    )

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return entityId
  }

  async findAll(ctx: Context, opts?: PlanReportFindOptions | undefined): Promise<PaginatedResult<ProductSupply>> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.PRODUCT_SUPPLIES_VIEW)

    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.productSuppliesRepository.findAll(tx, opts),
    )
  }

  async planReport(ctx: Context, opts?: PlanReportFindOptions | undefined): Promise<ProductReport[]> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.PRODUCT_PLAN_VIEW)

    const month = opts?.filter?.month || 0
    const year = opts?.filter?.year || this.opts.dateTimeProvider.get().getFullYear()
    const beginDate = new Date(year, month, 1)
    let endDate: Date
    if (Number.isInteger(opts?.filter?.month)) {
      endDate = new Date(new Date(year, month + 1, 1).getTime() - 1)
    } else {
      endDate = new Date(new Date(year + 1, 0, 1).getTime() - 1)
    }

    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const productSupplies = await this.opts.productSuppliesRepository.findAll(tx, {
        filter: {
          dateFrom: beginDate,
          dateTo: endDate,
        },
      })
      const actualIssueProducts = await this.opts.actualIssueProductsRepository.findAll(tx, {
        filter: {
          dateFrom: beginDate,
          dateTo: endDate,
        },
      })

      const categoryReports: ProductReport[] = []
      forIn(ProductCategory, productCategory => {
        let supply = 0
        productSupplies.items.filter(ps => ps.category === productCategory).forEach(ps => (supply += ps.amount))
        let consumption = 0
        actualIssueProducts.forEach(ap =>
          Object.entries(ap.values).forEach(val => (consumption += val[1][productCategory].actualConsumption)),
        )
        categoryReports.push({
          category: productCategory,
          supply: round(supply, 2),
          consumption: round(consumption, 2),
          balance: round(supply - consumption, 2),
        })
      })

      return categoryReports
    })
  }
}

export class ProductPlansModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(private opts: { productSuppliesRepository: ProductSuppliesRepository }) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    throwInvalidOperation('<2e44567c> Merge not supported')
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.PRODUCT_SUPPLY_ADD: {
        const p = event.payload as AddProductSupplyPayload
        await this.opts.productSuppliesRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          amount: p.amount,
          dataEntryDate: new Date(p.dataEntryDate),
          category: p.category,
        })
        return true
      }
    }
    return false
  }
}
